import { useCallback, useEffect, useRef } from 'react';
import { customWindow } from '../models/interfaces/wogaa.interface';
import { PolymerAxios } from '../utils/axios';
import { API_EligibilityDebugLog } from '../utils/api';
import { useAppSelector } from '../redux/hook';

interface UseWogaaOptions {
  trackingId: string;
  autoStart?: boolean;
  autoComplete?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
  /** Optional application UUID for server-side logging */
  applicationId?: string;
  /** Optional HDB reference for server-side logging */
  hdbRef?: string;
  /** Enable server-side debug logging (default: true) */
  enableServerLogging?: boolean;
}

/**
 * Custom hook for WOGAA transactional tracking with retry mechanism.
 * Handles the async nature of wogaaCustom initialization.
 * Includes server-side debug logging for tracking WOGAA events.
 * 
 * @param options - Configuration options for WOGAA tracking
 * @returns Object with start and complete functions for manual control
 */
export const useWogaa = ({
  trackingId,
  autoStart = false,
  autoComplete = false,
  retryAttempts = 10,
  retryDelay = 500,
  applicationId,
  hdbRef,
  enableServerLogging = true,
}: UseWogaaOptions) => {
  const hasStarted = useRef(false);
  const hasCompleted = useRef(false);
  
  // Get auth info for server-side logging
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

  /**
   * Log WOGAA event to server for debugging and analytics
   */
  const logToServer = useCallback(async (event: 'START' | 'COMPLETE' | 'ERROR', additionalInfo?: string) => {
    if (!enableServerLogging) return;
    
    // Build identifier string for better tracking
    const identifiers = [
      applicationId ? `appUuid: ${applicationId}` : null,
      hdbRef ? `hdbRef: ${hdbRef}` : null,
      loginUser?.uinfin ? `uinfin: ${loginUser.uinfin}` : null,
    ].filter(Boolean).join(', ');
    
    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken ?? "").post(API_EligibilityDebugLog, {
        applicationUuid: applicationId ?? loginUser?.uinfin,
        message: `FE WOGAA --- ${event} | trackingId: ${trackingId}${identifiers ? ` | ${identifiers}` : ''}${additionalInfo ? ` | ${additionalInfo}` : ''}`
      });
    } catch (error) {
      // Silently fail - don't block WOGAA tracking if logging fails
      console.warn(`[WOGAA] Server logging failed for ${event}:`, error);
    }
  }, [loginUser?.uinfin, singpassToken, trackingId, applicationId, hdbRef, enableServerLogging]);

  const waitForWogaa = useCallback(
    (callback: () => void, retriesLeft: number = retryAttempts) => {
      if (customWindow.wogaaCustom) {
        callback();
      } else if (retriesLeft > 0) {
        setTimeout(() => waitForWogaa(callback, retriesLeft - 1), retryDelay);
      } else {
        const errorMsg = `wogaaCustom not available after ${retryAttempts} attempts`;
        console.warn(`[WOGAA] ${errorMsg} for tracking ID: ${trackingId}`);
        logToServer('ERROR', errorMsg);
      }
    },
    [trackingId, retryAttempts, retryDelay, logToServer]
  );

  const startTransaction = useCallback(() => {
    if (hasStarted.current) {
      console.debug(`[WOGAA] Transaction already started for: ${trackingId}`);
      return;
    }
    
    waitForWogaa(() => {
      if (customWindow.wogaaCustom && !hasStarted.current) {
        console.debug(`[WOGAA] Starting transaction: ${trackingId}`);
        customWindow.wogaaCustom.startTransactionalService(trackingId);
        hasStarted.current = true;
        logToServer('START');
      }
    });
  }, [trackingId, waitForWogaa, logToServer]);

  const completeTransaction = useCallback(() => {
    if (hasCompleted.current) {
      console.debug(`[WOGAA] Transaction already completed for: ${trackingId}`);
      return;
    }
    
    waitForWogaa(() => {
      if (customWindow.wogaaCustom && !hasCompleted.current) {
        console.debug(`[WOGAA] Completing transaction: ${trackingId}`);
        customWindow.wogaaCustom.completeTransactionalService(trackingId);
        hasCompleted.current = true;
        logToServer('COMPLETE');
      }
    });
  }, [trackingId, waitForWogaa, logToServer]);

  // Auto-start transaction on mount if enabled
  useEffect(() => {
    if (autoStart) {
      startTransaction();
    }
  }, [autoStart, startTransaction]);

  // Auto-complete transaction on mount if enabled
  useEffect(() => {
    if (autoComplete) {
      completeTransaction();
    }
  }, [autoComplete, completeTransaction]);

  // Reset refs when trackingId changes
  useEffect(() => {
    hasStarted.current = false;
    hasCompleted.current = false;
  }, [trackingId]);

  return {
    startTransaction,
    completeTransaction,
  };
};

export default useWogaa;
